import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { FILM_LOOK_PRESETS } from '../../lib/project';
import { Button } from '../../ui/kit/components/primitives';
import { TextArea, TextField, SelectField } from '../../ui/kit/components/forms';
import type { ProjectKind } from '../../components/NewProjectDialog';
import MicrodramaFields from './MicrodramaFields';
import type { SeriesStartMode } from '../../lib/series';

const TEXT_SCRIPT = new Set(['.txt', '.md', '.fountain', '.fdx']);
const PARSED_SCRIPT = new Set(['.pdf', '.docx']);

function scriptExtension(name: string) {
  return name.toLowerCase().match(/(\.[a-z0-9]+)$/)?.[1] || '';
}

function needsParsedExtract(file: File) {
  const ext = scriptExtension(file.name);
  return PARSED_SCRIPT.has(ext) || file.type === 'application/pdf' || /wordprocessingml|officedocument/.test(file.type);
}

function looksLikeBinaryDump(text: string) {
  const sample = text.slice(0, 4000);
  if (sample.startsWith('%PDF')) return true;
  if (sample.includes('\u0000')) return true;
  const replacement = (sample.match(/\uFFFD/g) || []).length;
  const control = [...sample].filter((ch) => {
    const code = ch.charCodeAt(0);
    return code < 9 || (code > 13 && code < 32);
  }).length;
  return replacement > 8 || control > 20;
}

async function readAttachedScreenplay(file: File) {
  const ext = scriptExtension(file.name);
  if (!TEXT_SCRIPT.has(ext) && !PARSED_SCRIPT.has(ext) && !needsParsedExtract(file)) {
    throw new Error('Use PDF, Word, Final Draft, Fountain, TXT, or Markdown.');
  }
  if (needsParsedExtract(file)) {
    const extracted = await api.extractScreenplayFile(file);
    const text = String(extracted.text || '').trim();
    if (!text) throw new Error('No readable screenplay text was found in that file.');
    return text;
  }
  const text = await file.text();
  if (looksLikeBinaryDump(text)) {
    const extracted = await api.extractScreenplayFile(file);
    const parsed = String(extracted.text || '').trim();
    if (!parsed) throw new Error('That file is not readable as text. Export a PDF or Word screenplay and try again.');
    return parsed;
  }
  return text;
}

interface Props {
  kind: ProjectKind;
  copy: { types: string[]; allowCustomType: boolean; showRuntime: boolean; ideaLabel: string; ideaPlaceholder: string };
  formats: string[];
  useCustomType: boolean;
  onCustomType: (value: boolean) => void;
  lookPreset: string;
  onLook: (value: string) => void;
  script: string;
  scriptFilename: string;
  onScript: (value: string, filename?: string) => void;
  onError: (value: string) => void;
  series?: { active: boolean; startMode: SeriesStartMode; onStartMode: (mode: SeriesStartMode) => void; onType: (type: string) => void };
}

export default function ProjectSetupFields({ kind, copy, formats, useCustomType, onCustomType, lookPreset, onLook, script, scriptFilename, onScript, onError, series }: Props) {
  const microdrama = Boolean(series?.active);
  const fileInput = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  return <div className="studio-project-setup-fields">
    <TextField label="Project name" name="name" placeholder="Untitled project" required autoFocus />
    <SelectField label={kind === 'film' ? 'What are you making?' : 'Work type'} name="type" options={[...copy.types.map(type => ({ value: type, label: type })), ...(copy.allowCustomType ? [{ value: '__other__', label: 'Other (type your own)' }] : [])]} onChange={event => { onCustomType(event.target.value === '__other__'); series?.onType(event.target.value); }} />
    {copy.allowCustomType && useCustomType && <TextField label="Custom work type" name="customType" required placeholder="Web series, feature film, commercial…" />}
    <SelectField key={microdrama ? 'series-format' : 'format'} label={kind === 'vfx' ? 'Delivery frame' : 'Screen format'} name="format" defaultValue={microdrama ? '9:16' : '16:9'} options={formats.map(format => ({ value: format, label: format }))} hint={kind === 'saas' ? 'Your preview and export use this frame. Original screenshots keep their aspect ratio.' : 'Shot stills, image-to-video and the editor use this frame. World sheets stay 1:1.'} />
    {microdrama && series ? <MicrodramaFields startMode={series.startMode} onStartMode={series.onStartMode} /> : copy.showRuntime ? <TextField label="Target length (minutes)" name="runtime" type="number" min="0.5" max="300" step="0.5" defaultValue={5} hint="Drives the shot count and generation budget. Change it any time." /> : <SelectField label="Frame rate" name="fps" defaultValue="24" options={[23.976, 24, 25, 30].map(fps => ({ value: String(fps), label: String(fps) }))} />}
    {kind === 'film' && <>
      <TextField label="Target budget (INR)" name="budgetInr" type="number" min="1000" step="1000" defaultValue={25000} required hint="You can go over it. Nothing stops at the target; the estimate and meter show where you stand." />
      <SelectField label="Visual look" value={lookPreset} onChange={event => onLook(event.target.value)} options={FILM_LOOK_PRESETS.map(preset => ({ value: preset.id, label: preset.label }))} hint="Drives character and location sheets. Changing this later may require regenerating sheets." />
      {!microdrama && <div className="studio-project-setup-wide">
        <TextArea label="Screenplay (optional)" rows={5} value={script} onChange={event => onScript(event.target.value, event.target.value.trim() ? scriptFilename : '')} placeholder="Paste a screenplay or attach a file. Cue can extract cast, places, props, scenes and a story summary." />
        <input ref={fileInput} type="file" accept=".txt,.md,.fountain,.fdx,.pdf,.docx,application/pdf" hidden onChange={async event => {
          const input = event.currentTarget, file = input.files?.[0];
          if (!file || reading) return;
          setReading(true);
          onError('');
          try {
            onScript(await readAttachedScreenplay(file), file.name);
          } catch (cause) {
            onScript('', '');
            onError(cause instanceof Error ? cause.message : 'Could not read the screenplay file. Try another file.');
          }
          setReading(false);
          input.value = '';
        }} />
        <div className="bs-between"><Button size="sm" disabled={reading} onClick={() => fileInput.current?.click()}>{reading ? 'Reading file…' : 'Attach a file'}</Button>{scriptFilename && <span className="bs-small">{scriptFilename}</span>}{script.trim() && <Button size="sm" disabled={reading} onClick={() => onScript('', '')}>Remove screenplay</Button>}</div>
        <p className="bs-small bs-muted">{reading ? 'Extracting text from the attached file…' : script.trim() ? 'Cue analyses this when the project is created. Review the estimate before continuing.' : 'No screenplay? Leave this empty and shape the story with Cue in chat.'}</p>
      </div>}
    </>}
    <TextArea className="studio-project-setup-wide" label={microdrama ? 'What is the series about?' : copy.ideaLabel} name="idea" placeholder={microdrama ? 'Premise, characters and central conflict in your own words. Anything you leave out, Cue asks about.' : copy.ideaPlaceholder} required={kind !== 'film' || microdrama} />
  </div>;
}
